/* eslint-env browser */
import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  type ReactNode
} from 'react';
import { type Socket, io } from 'socket.io-client';
import { useAppDispatch, useAppSelector } from './hooks';
import {
  selectSocketAddress,
  changeSocketConnected
} from '../redux/settingsSlice';
import {
  selectJudge,
  updateJudgesList,
  selectSocketsCache,
  updateTask,
  updateTaskManagerQueue,
  updateTaskResults,
  updateParticipantResults,
  clearParticipantResultsForRound,
  removeTaskCompletedByRound,
  type SocketElement,
  type JudgesListSettings,
  type ManagerItem
} from '../redux/workSlice';

import {
  addTimetableDetails,
  setActiveCompetition,
  setTimetable,
  addMonitorDetails,
  clearMonitorDetails,
  type TimetableDetails,
  type TimetableItem,
  type Competition
} from '../redux/timetableSlice';
import {
  setVisualState,
  applyVisualControl,
  type VisualState,
  type VisualControlCommand
} from '../redux/visualSlice';
import {
  setJudgesStatus,
  type JudgesStatusPayload
} from '../redux/judgesStatusSlice';
import {
  selectDeviceUuid,
  selectDeviceId,
  selectDeviceType,
  changeSelectedHall
} from '../redux/settingsSlice';
import SOCKET from '../constants/socketTypes';
import DEVICE from '../constants/deviceType';

export const SocketContext = createContext<Socket | null>(null);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [isConnected, setConnected] = useState(false);
  const [isSocketInitialized, setSocketInitialized] = useState(false);

  const socket = useRef<Socket | null>(null);
  const dispatch = useAppDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOnMessage = (_message?: unknown): void => {
    // store.dispatch here
  };
  useEffect(() => {
    dispatch(changeSocketConnected(isConnected));
  }, [isConnected]);

  const socketUrl = useAppSelector(selectSocketAddress);
  const socketsCache = useAppSelector(selectSocketsCache);
  const chosenJudge = useAppSelector(selectJudge);
  const chosenJudgeRef = useRef(chosenJudge);
  const uuid = useAppSelector(selectDeviceUuid);
  const deviceId = useAppSelector(selectDeviceId);
  const deviceType = useAppSelector(selectDeviceType);
  const deviceTypeRef = useRef(deviceType);
  const activeCompetition = useAppSelector(
    (state) => state.timetable?.activeCompetition
  );
  const prevCompetitionUuidRef = useRef<string | undefined>(activeCompetition?.uuid);

  useEffect(() => {
    chosenJudgeRef.current = chosenJudge;
  }, [chosenJudge]);

  useEffect(() => {
    deviceTypeRef.current = deviceType;
  }, [deviceType]);

  // Reset hall selection when competition changes
  useEffect(() => {
    const currentUuid = activeCompetition?.uuid;
    if (prevCompetitionUuidRef.current && currentUuid && currentUuid !== prevCompetitionUuidRef.current) {
      dispatch(changeSelectedHall('all'));
    }
    prevCompetitionUuidRef.current = currentUuid;
  }, [activeCompetition?.uuid]);

  useEffect(() => {
    let reconnectInterval: ReturnType<typeof setInterval> | undefined;

    try {
      if (!isConnected && !isSocketInitialized) {
        socket.current = io(socketUrl, {
          transports: ['websocket', 'webtransport'],
          rejectUnauthorized: false
        });

        socket.current?.on('connect', () => {
          try {
            setSocketInitialized(true); // Mark socket as initialized

            if (chosenJudge?.uuid && chosenJudge?.letter && chosenJudge?.name) {
              const adjudicatorSocket: SocketElement = {
                action: SOCKET.ADJUDICATOR,
                args: [
                  {
                    type: SOCKET.ADJUDICATOR_LOG_IN,
                    letter: chosenJudge.letter,
                    name: chosenJudge.name,
                    adjudicatorUuid: chosenJudge.uuid,
                    socketId: socket.current?.id,
                    deviceUuid: uuid,
                    deviceId: deviceId
                  }
                ],
                acknowledge: false,
                volatile: false
              };
              socket.current?.emit(
                adjudicatorSocket.action,
                ...adjudicatorSocket.args
              );
            }

            socketsCache.forEach((s: SocketElement) => {
              try {
                if (s.action === SOCKET.ADJUDICATOR) {
                  s.socketId = socket.current?.id;
                }
                if (s.volatile) {
                  socket.current?.volatile?.emit(s.action, ...s.args);
                } else {
                  socket.current?.emit(s.action, ...s.args);
                }
              } catch (err) {
                console.error(
                  'Error emitting socket action:',
                  (err as Error).message
                );
              }
            });

            setConnected(true);
          } catch (err) {
            console.error(
              'Error during socket connection setup:',
              (err as Error).message
            );
          }
        });

        socket.current?.on('disconnect', (reason) => {
          if (socket.current?.active) {
            console.log(
              'Socket disconnected temporarily, attempting to reconnect...'
            );
            setConnected(false);
            // temporary disconnection, the socket will automatically try to reconnect
          } else {
            // the connection was forcefully closed by the server or the client itself
            // in that case, `socket.connect()` must be manually called in order to reconnect
            try {
              setConnected(false);
              setSocketInitialized(false); // Reset initialization state on disconnect
            } catch (err) {
              console.error(
                'Error during socket disconnect handling:',
                (err as Error).message
              );
            }
            console.log(reason);
            socket.current?.connect();
          }
        });
        // socket.current?.onAny((event, ...args) => {
        //   // Handle any event that is not explicitly handled
        //   console.log(`Socket event: ${event}`, args);
// console.log(deviceTypeRef.current);
        // });
        socket.current?.on(SOCKET.MESSAGE, (message) => {
          try {
            handleOnMessage(message);
          } catch (err) {
            console.error(
              'Error handling SOCKET.MESSAGE:',
              (err as Error).message
            );
          }
        });

        socket.current?.on(
          SOCKET.TIMETABLE_DETAILS,
          (arg: TimetableDetails) => {
            try {
              if (
                deviceTypeRef.current === DEVICE.MC ||
                deviceTypeRef.current === DEVICE.CHAIRPERSON ||
                deviceTypeRef.current === DEVICE.DJ ||
                deviceTypeRef.current === DEVICE.FLOOR_MANAGER ||
                deviceTypeRef.current === DEVICE.LED
              ) {
                dispatch(addTimetableDetails(arg));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.TIMETABLE_DETAILS:',
                (err as Error).message
              );
            }
          }
        );
        socket.current?.on(SOCKET.RESET_MONITOR, () => {
          try {
            dispatch(clearMonitorDetails());
          } catch (err) {
            console.error(
              'Error handling SOCKET.RESET_MONITOR:',
              (err as Error).message
            );
          }
        });
        socket.current?.on(SOCKET.MONITOR_DETAILS, (arg: TimetableDetails) => {
          try {
            if (deviceTypeRef.current === DEVICE.MONITOR) {
              dispatch(addMonitorDetails(arg));
            }
          } catch (err) {
            console.error(
              'Error handling SOCKET.MONITOR_DETAILS:',
              (err as Error).message
            );
          }
        });
        socket.current?.on(
          SOCKET.JUDGES_LIST,
          (arg: object, settings: JudgesListSettings) => {
            // console.log('Judges List:', arg, settings);
            try {
              dispatch(
                updateJudgesList({ list: arg, settings: settings || {} })
              );
            } catch (err) {
              console.error(
                'Error handling SOCKET.JUDGES_LIST:',
                (err as Error).message
              );
            }
          }
        );

        socket.current?.on(SOCKET.TIMETABLE, (arg: TimetableItem[]) => {
          try {
            dispatch(setTimetable(arg));
          } catch (err) {
            console.error(
              'Error handling SOCKET.TIMETABLE:',
              (err as Error).message
            );
          }
        });
        socket.current?.on(SOCKET.COMPETITION, (arg: Competition) => {
          try {
            dispatch(setActiveCompetition(arg));
          } catch (err) {
            console.error(
              'Error handling SOCKET.COMPETITION:',
              (err as Error).message
            );
          }
        });

        // Visual Control Events (LED/Broadcast)
        socket.current?.on(SOCKET.VISUAL_STATE, (state: VisualState) => {
          try {
            dispatch(setVisualState(state));
          } catch (err) {
            console.error(
              'Error handling SOCKET.VISUAL_STATE:',
              (err as Error).message
            );
          }
        });

        socket.current?.on(
          SOCKET.VISUAL_CONTROL,
          (command: VisualControlCommand) => {
            try {
              dispatch(applyVisualControl(command));
            } catch (err) {
              console.error(
                'Error handling SOCKET.VISUAL_CONTROL:',
                (err as Error).message
              );
            }
          }
        );

        // Judges status updates (for chairperson and MC)
        socket.current?.on(
          SOCKET.JUDGES_STATUS,
          (payload: JudgesStatusPayload) => {
            try {
              if (
                deviceTypeRef.current === DEVICE.CHAIRPERSON ||
                deviceTypeRef.current === DEVICE.MC
              ) {
                //  console.log('Received JUDGES_STATUS update:', payload);
                dispatch(setJudgesStatus(payload));
              }
            } catch (err) {
              console.error(
                'Error handling SOCKET.JUDGES_STATUS:',
                (err as Error).message
              );
            }
          }
        );

        // Buffer to prevent duplicate task handling
        const taskBuffer = new Map<string, number>();
        const TASK_BUFFER_TIME = 2000; // 2 seconds

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        socket.current?.on(SOCKET.ADJUDICATOR_SHEET, (task: any) => {
          try {
            if (!task) {
              throw new Error('Invalid task data received');
            }
            // Check buffer for duplicate task
            const now = Date.now();
            const lastReceived = taskBuffer.get(task.uuid);
            if (lastReceived && now - lastReceived < TASK_BUFFER_TIME) {
              // Ignore duplicate within buffer time
              return;
            }
            taskBuffer.set(task.uuid, now);
            // Clean up old entries
            for (const [uuid, timestamp] of taskBuffer) {
              if (now - timestamp > TASK_BUFFER_TIME) {
                taskBuffer.delete(uuid);
              }
            }
            if (chosenJudgeRef.current?.uuid === task.adjudicatorUuid) {
              // If the task is being re-sent for revision (status is not done),
              // clear any previous completion record so isRoundFinished returns false
              if (task.taskResults?.status !== 'done') {
                dispatch(
                  removeTaskCompletedByRound({
                    roundUuid: task.roundUuid,
                    adjudicatorUuid: task.adjudicatorUuid
                  })
                );
              }

              dispatch(updateTask(task));
              dispatch(updateTaskResults(task.taskResults));
              if (task.taskResults.participantResults.length === 0) {
                dispatch(clearParticipantResultsForRound(task));
              } else {
                dispatch(
                  updateParticipantResults(task.taskResults.participantResults)
                );
              }
              socket.current?.emit(SOCKET.ACK_ADJUDICATOR_SHEET, {
                type: SOCKET.ACK_ADJUDICATOR_SHEET,
                uuid: task.uuid,
                adjudicatorUuid: chosenJudgeRef.current?.uuid,
                roundUuid: task.roundUuid
              });
            }
          } catch (err) {
            console.error(
              'Error handling SOCKET.ADJUDICATOR_SHEET:',
              (err as Error).message
            );
          }
        });

        socket.current?.on(
          SOCKET.TASK_MANAGET_QUEUE,
          (queue: ManagerItem[]) => {
            try {
              if (!queue) {
                throw new Error('Invalid queue data received');
              }
              // console.log('Task Manager Queue:', queue);
              dispatch(updateTaskManagerQueue(queue));
            } catch (err) {
              console.error(
                'Error handling SOCKET.TASK_MANAGET_QUEUE:',
                (err as Error).message
              );
            }
          }
        );
        socket.current?.on('connect_error', (error: Error) => {
          if (socket.current?.active) {
            // temporary failure, the socket will automatically try to reconnect
          } else {
            // the connection was denied by the server
            // in that case, `socket.connect()` must be manually called in order to reconnect
            console.log(error.message);
            try {
              socket.current?.connect();
            } catch (err) {
              console.error(
                'Error during socket reconnection:',
                (err as Error).message
              );
            }
          }
        });

        socket.current?.on(
          'error',
          function transmit_server_connection_handshake_error() {
            return null;
          }
        );
      }
    } catch (err) {
      console.error(
        'Error during socket initialization:',
        (err as Error).message
      );
    }

    return () => {
      if (socket.current) {
        try {
          socket.current.off('connect');
          socket.current.off('disconnect');
          socket.current.off(SOCKET.MESSAGE);
          socket.current.off(SOCKET.TIMETABLE_DETAILS);
          socket.current.off(SOCKET.JUDGES_LIST);
          socket.current.off(SOCKET.TIMETABLE);
          socket.current.off(SOCKET.ADJUDICATOR_SHEET);
          socket.current.off(SOCKET.TASK_MANAGET_QUEUE);
          socket.current.off(SOCKET.VISUAL_STATE);
          socket.current.off(SOCKET.VISUAL_CONTROL);
          socket.current.off('error');
        } catch (err) {
          console.error('Error during socket cleanup:', (err as Error).message);
        }

        if (socket.current.connected) {
          socket.current.disconnect();
        }
      }

      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [socketUrl]);

  return (
    <SocketContext.Provider value={socket.current}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): Socket | null => useContext(SocketContext);
